import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tab from "@mui/material/Tab";
import { styled, alpha } from "@mui/material/styles";
import {
  TextField,
  Input,
  InputAdornment,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  Grid,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  CloudUpload,
  Lock,
  Settings,
  Person,
} from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";
import Slide from "@mui/material/Slide";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import useApp from "../../store/contexts/AppContext";

// Custom transition for dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Styled components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 12,
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
    overflow: "hidden",
    [theme.breakpoints.up("sm")]: {
      maxWidth: 560,
      width: "100%",
    },
  },
  "& .MuiDialogTitle-root": {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.mode === "dark" ? "#1E1E1E" : "#f9f9f9",
    borderBottom: `1px solid ${
      theme.palette.mode === "dark" ? "#333" : "#e0e0e0"
    }`,
  },
  "& .MuiDialogContent-root": {
    padding: theme.spacing(3),
  },
}));

const StyledTabPanel = styled(TabPanel)(({ theme }) => ({
  padding: theme.spacing(3, 0),
  animation: "fadeIn 0.3s ease-in-out",
  "@keyframes fadeIn": {
    from: {
      opacity: 0,
      transform: "translateY(10px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
}));

const StyledTabList = styled(TabList)(({ theme }) => ({
  "& .MuiTabs-indicator": {
    height: 3,
    borderRadius: 1.5,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  "& .MuiTab-root": {
    textTransform: "none",
    fontSize: 14,
    fontWeight: 500,
    minWidth: "auto",
    padding: theme.spacing(1.5, 2),
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&.Mui-selected": {
      fontWeight: 600,
      color: theme.palette.primary.main,
    },
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      borderRadius: 8,
      transform: "translateY(-1px)",
    },
  },
}));

const StyledSettingsItem = styled(Paper)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2, 3),
  borderRadius: 8,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  backgroundColor: theme.palette.mode === "dark" ? "#262626" : "#fff",
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 2px 8px rgba(0, 0, 0, 0.2)"
      : "0 2px 8px rgba(0, 0, 0, 0.05)",
  "&:hover": {
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 4px 12px rgba(0, 0, 0, 0.3)"
        : "0 4px 12px rgba(0, 0, 0, 0.1)",
    transform: "translateY(-2px)",
  },
}));

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const ImagePreviewContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  borderRadius: 8,
  backgroundColor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5",
  border: `1px dashed ${theme.palette.mode === "dark" ? "#444" : "#ccc"}`,
  transition: "all 0.3s ease",
  animation: "slideUp 0.3s ease-out",
  "@keyframes slideUp": {
    from: {
      opacity: 0,
      transform: "translateY(10px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
  "&:hover": {
    borderColor: theme.palette.mode === "dark" ? "#666" : "#999",
    backgroundColor: theme.palette.mode === "dark" ? "#222" : "#efefef",
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: theme.spacing(1, 3),
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "none",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    boxShadow: theme.palette.mode === "dark"
      ? "0 4px 16px rgba(212, 68, 1, 0.3)"
      : "0 4px 12px rgba(0, 0, 0, 0.15)",
    transform: "translateY(-2px)",
  },
  "&.MuiButton-contained": {
    backgroundColor: theme.palette.primary.main,
    color: "#ffffff",
    "&:hover": {
      backgroundColor: theme.palette.mode === "dark" ? "#e64f01" : "#b83a01",
    },
  },
  "&.MuiButton-outlined": {
    borderColor: theme.palette.mode === "dark"
      ? "rgba(212, 68, 1, 0.5)"
      : theme.palette.primary.main,
    color: theme.palette.mode === "dark"
      ? theme.palette.primary.main
      : theme.palette.text.primary,
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.mode === "dark"
        ? "rgba(212, 68, 1, 0.08)"
        : "rgba(212, 68, 1, 0.04)",
    },
  },
  "&.Mui-disabled": {
    opacity: 0.5,
    backgroundColor: theme.palette.mode === "dark" ? "#2a2a2a" : "#e0e0e0",
    color: theme.palette.mode === "dark" ? "#666" : "#999",
  },
}));

export default function EditProfileModal({
  openEditProfile,
  handleEditProfileOpen,
  handleEditProfileClose,
  classes,
  setFetchedUserData,
}) {
  const {
    appState: { userData },
    changeUserPasswordApiCall,
    changeUserProfilePictureApiCall,
    changeUserCoverPictureApiCall,
    updateUserProfileApiCall,
    toggleUserEmailNotificationStatusApiCall,
    showSnackbar,
  } = useApp();

  const [tabValue, setTabValue] = React.useState("1");
  const [hasEmailNotification, setHasEmailNotification] = React.useState(
    userData?.has_email_notification ? true : false
  );
  const [showPassword1, setShowPassword1] = React.useState(false);
  const [showPassword2, setShowPassword2] = React.useState(false);
  const [showPassword3, setShowPassword3] = React.useState(false);
  const [apiRequestIsLoading, setApiRequestIsLoading] = React.useState(false);
  const [selectedProfilePicture, setSelectedProfilePicture] =
    React.useState(null);
  const [selectedCoverPicture, setSelectedCoverPicture] = React.useState(null);
  const [profilePreviewUrl, setProfilePreviewUrl] = React.useState(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = React.useState(null);

  // Profile & Bio state
  const [profileData, setProfileData] = React.useState({
    bio: userData?.bio || "",
    display_name: userData?.display_name || "",
    location: userData?.location || "",
    date_of_birth: userData?.date_of_birth || "",
    gender: userData?.gender || "",
    language_preference: userData?.language_preference || "en",
    timezone: userData?.timezone || "",
    website: userData?.website || "",
    social_media_links: userData?.social_media_links || {
      twitter: "",
      instagram: "",
      linkedin: "",
      facebook: "",
    },
  });

  // Update profile data when userData changes
  React.useEffect(() => {
    if (userData) {
      setProfileData({
        bio: userData?.bio || "",
        display_name: userData?.display_name || "",
        location: userData?.location || "",
        date_of_birth: userData?.date_of_birth || "",
        gender: userData?.gender || "",
        language_preference: userData?.language_preference || "en",
        timezone: userData?.timezone || "",
        website: userData?.website || "",
        social_media_links: userData?.social_media_links || {
          twitter: "",
          instagram: "",
          linkedin: "",
          facebook: "",
        },
      });
    }
  }, [userData]);

  const profileInputRef = React.useRef(null);
  const coverInputRef = React.useRef(null);
  const currentPasswordRef = React.useRef(null);
  const newPasswordRef = React.useRef(null);
  const confirmPasswordRef = React.useRef(null);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleProfilePictureChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedProfilePicture(file);
      const reader = new FileReader();
      reader.onload = () => {
        setProfilePreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCoverPictureChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedCoverPicture(file);
      const reader = new FileReader();
      reader.onload = () => {
        setCoverPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // No need for separate button click handlers
  // The buttons will use the component="label" prop to handle the click behavior

  const handleShowPassword1 = () => {
    setShowPassword1(!showPassword1);
  };

  const handleShowPassword2 = () => {
    setShowPassword2(!showPassword2);
  };

  const handleShowPassword3 = () => {
    setShowPassword3(!showPassword3);
  };

  const handlePicturesUpload = async (event) => {
    event.preventDefault();

    if (apiRequestIsLoading) {
      return showSnackbar(
        "You still have an ongoing process. Please wait and try again."
      );
    }

    setApiRequestIsLoading(true);

    //Check if user wants to change profile picture or has any selected
    if (selectedProfilePicture) {
      // Create FormData object
      const formData = new FormData();
      formData.append("profile_image", selectedProfilePicture);

      //Make API call to change profile picture
      const apiResult = await changeUserProfilePictureApiCall(formData);

      //Check if api call succeeded
      if (apiResult.success) {
        setFetchedUserData((prev) => ({
          ...prev,
          profile_image_url: apiResult.data.profile_image_url,
        }));
        setSelectedProfilePicture(null);
        setProfilePreviewUrl(null);
      }
    }

    //Check if user wants to change cover picture or has any selected
    if (selectedCoverPicture) {
      // Create FormData object
      const formData = new FormData();
      formData.append("cover_image", selectedCoverPicture);

      //Make API call to change cover picture
      const apiResult = await changeUserCoverPictureApiCall(formData);

      //Check if api call succeeded
      if (apiResult.success) {
        setFetchedUserData((prev) => ({
          ...prev,
          cover_image_url: apiResult.data.cover_image_url,
        }));
        setSelectedCoverPicture(null);
        setCoverPreviewUrl(null);
      }
    }

    setApiRequestIsLoading(false);
  };

  const handleChangeOfPassword = async (event) => {
    event.preventDefault();

    if (apiRequestIsLoading) {
      return showSnackbar(
        "You still have an ongoing process. Please wait and try again."
      );
    }

    setApiRequestIsLoading(true);

    const currentPassword = currentPasswordRef.current.value;
    const newPassword = newPasswordRef.current.value;
    const confirmPassword = confirmPasswordRef.current.value;

    const dataToPass = {
      old_password: currentPassword,
      password: newPassword,
      confirm_password: confirmPassword,
    };

    const changePasswordRequest = await changeUserPasswordApiCall(dataToPass);

    if (changePasswordRequest.success) {
      currentPasswordRef.current.value = "";
      newPasswordRef.current.value = "";
      confirmPasswordRef.current.value = "";
    }

    setApiRequestIsLoading(false);
  };

  const handleChangeOfEmailNotificationStatus = async () => {
    const runApiUpdate = await toggleUserEmailNotificationStatusApiCall();
    if (runApiUpdate.success) {
      setHasEmailNotification(!hasEmailNotification);
    }
  };

  const handleProfileDataChange = (field, value) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSocialMediaChange = (platform, value) => {
    setProfileData((prev) => ({
      ...prev,
      social_media_links: {
        ...prev.social_media_links,
        [platform]: value,
      },
    }));
  };

  const handleUpdateProfile = async (event) => {
    event.preventDefault();

    if (apiRequestIsLoading) {
      return showSnackbar(
        "You still have an ongoing process. Please wait and try again."
      );
    }

    setApiRequestIsLoading(true);

    const updateResult = await updateUserProfileApiCall(profileData);

    if (updateResult.success && setFetchedUserData) {
      setFetchedUserData(updateResult.data);
    }

    setApiRequestIsLoading(false);
  };

  return (
    <StyledDialog
      onClose={handleEditProfileClose}
      aria-labelledby="customized-dialog-title"
      open={openEditProfile}
      TransitionComponent={Transition}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="customized-dialog-title" sx={{ m: 0, p: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          Edit Profile
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleEditProfileClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: (theme) => theme.palette.grey[500],
            borderRadius: "50%",
            p: 1,
            "&:hover": {
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.08)"
                  : "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TabContext value={tabValue}>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <StyledTabList
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab
                icon={<Person fontSize="small" />}
                iconPosition="start"
                label="Profile & Bio"
                value="1"
              />
              <Tab
                icon={<CloudUpload fontSize="small" />}
                iconPosition="start"
                label="Pictures"
                value="2"
              />
              <Tab
                icon={<Lock fontSize="small" />}
                iconPosition="start"
                label="Change Password"
                value="3"
              />
              <Tab
                icon={<Settings fontSize="small" />}
                iconPosition="start"
                label="Settings"
                value="4"
              />
            </StyledTabList>
          </Box>

          <StyledTabPanel value="1">
            <Box
              component="form"
              noValidate
              autoComplete="off"
              onSubmit={handleUpdateProfile}
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <Grid container spacing={2}>
                {/* Bio */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Bio"
                    multiline
                    rows={4}
                    value={profileData.bio}
                    onChange={(e) => handleProfileDataChange("bio", e.target.value)}
                    placeholder="Tell us about yourself..."
                    variant="outlined"
                  />
                </Grid>

                {/* Display Name */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Display Name"
                    value={profileData.display_name}
                    onChange={(e) => handleProfileDataChange("display_name", e.target.value)}
                    placeholder="Your display name"
                    variant="outlined"
                  />
                </Grid>

                {/* Location */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Location"
                    value={profileData.location}
                    onChange={(e) => handleProfileDataChange("location", e.target.value)}
                    placeholder="City, Country"
                    variant="outlined"
                  />
                </Grid>

                {/* Date of Birth */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Date of Birth"
                    type="date"
                    value={profileData.date_of_birth}
                    onChange={(e) => handleProfileDataChange("date_of_birth", e.target.value)}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {/* Gender */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Gender</InputLabel>
                    <Select
                      value={profileData.gender}
                      onChange={(e) => handleProfileDataChange("gender", e.target.value)}
                      label="Gender"
                    >
                      <MenuItem value="">
                        <em>Prefer not to say</em>
                      </MenuItem>
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                      <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Language Preference */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Language Preference</InputLabel>
                    <Select
                      value={profileData.language_preference}
                      onChange={(e) => handleProfileDataChange("language_preference", e.target.value)}
                      label="Language Preference"
                    >
                      <MenuItem value="en">English</MenuItem>
                      <MenuItem value="es">Spanish</MenuItem>
                      <MenuItem value="fr">French</MenuItem>
                      <MenuItem value="de">German</MenuItem>
                      <MenuItem value="zh">Chinese</MenuItem>
                      <MenuItem value="ja">Japanese</MenuItem>
                      <MenuItem value="ko">Korean</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Timezone */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Timezone"
                    value={profileData.timezone}
                    onChange={(e) => handleProfileDataChange("timezone", e.target.value)}
                    placeholder="e.g., America/New_York"
                    variant="outlined"
                  />
                </Grid>

                {/* Website */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Website"
                    value={profileData.website}
                    onChange={(e) => handleProfileDataChange("website", e.target.value)}
                    placeholder="https://yourwebsite.com"
                    variant="outlined"
                  />
                </Grid>

                {/* Social Media Links */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 1 }}>
                    Social Media Links
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Twitter"
                    value={profileData.social_media_links?.twitter || ""}
                    onChange={(e) => handleSocialMediaChange("twitter", e.target.value)}
                    placeholder="https://twitter.com/username"
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Instagram"
                    value={profileData.social_media_links?.instagram || ""}
                    onChange={(e) => handleSocialMediaChange("instagram", e.target.value)}
                    placeholder="https://instagram.com/username"
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="LinkedIn"
                    value={profileData.social_media_links?.linkedin || ""}
                    onChange={(e) => handleSocialMediaChange("linkedin", e.target.value)}
                    placeholder="https://linkedin.com/in/username"
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Facebook"
                    value={profileData.social_media_links?.facebook || ""}
                    onChange={(e) => handleSocialMediaChange("facebook", e.target.value)}
                    placeholder="https://facebook.com/username"
                    variant="outlined"
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                <StyledButton
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={apiRequestIsLoading}
                  startIcon={
                    apiRequestIsLoading && (
                      <CircularProgress size={16} color="inherit" />
                    )
                  }
                >
                  {apiRequestIsLoading ? "Saving..." : "Save Profile"}
                </StyledButton>
              </Box>
            </Box>
          </StyledTabPanel>

          <StyledTabPanel value="2">
            <Box
              component="form"
              noValidate
              autoComplete="off"
              onSubmit={handlePicturesUpload}
              sx={{ display: "flex", flexDirection: "column", gap: 3 }}
            >
              {/* Profile Picture Section */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="subtitle1" fontWeight={500}>
                  Profile Picture
                </Typography>

                <StyledButton
                  component="label"
                  variant="outlined"
                  color="primary"
                  startIcon={<CloudUpload />}
                  sx={{ alignSelf: "flex-start", mb: 1 }}
                >
                  Select Image
                  <VisuallyHiddenInput
                    type="file"
                    accept="image/jpeg, image/png, image/gif"
                    ref={profileInputRef}
                    onChange={handleProfilePictureChange}
                  />
                </StyledButton>

                {(profilePreviewUrl || userData?.profile_image_url) && (
                  <ImagePreviewContainer>
                    <Avatar
                      src={profilePreviewUrl || userData?.profile_image_url}
                      alt="Profile Picture Preview"
                      sx={{
                        width: 100,
                        height: 100,
                        mb: 1,
                        transition: "transform 0.3s ease",
                        "&:hover": {
                          transform: "scale(1.05)",
                        },
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {profilePreviewUrl
                        ? "New profile picture selected"
                        : "Current profile picture"}
                    </Typography>
                  </ImagePreviewContainer>
                )}
              </Box>

              {/* Cover Picture Section */}
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}
              >
                <Typography variant="subtitle1" fontWeight={500}>
                  Cover Picture
                </Typography>

                <StyledButton
                  component="label"
                  variant="outlined"
                  color="primary"
                  startIcon={<CloudUpload />}
                  sx={{ alignSelf: "flex-start", mb: 1 }}
                >
                  Select Image
                  <VisuallyHiddenInput
                    type="file"
                    accept="image/jpeg, image/png, image/gif"
                    ref={coverInputRef}
                    onChange={handleCoverPictureChange}
                  />
                </StyledButton>

                {(coverPreviewUrl || userData?.cover_image_url) && (
                  <ImagePreviewContainer>
                    <Box
                      component="img"
                      src={coverPreviewUrl || userData?.cover_image_url}
                      alt="Cover Picture Preview"
                      sx={{
                        width: "100%",
                        height: 120,
                        objectFit: "cover",
                        borderRadius: 1,
                        transition: "transform 0.3s ease",
                        "&:hover": {
                          transform: "scale(1.02)",
                        },
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {coverPreviewUrl
                        ? "New cover picture selected"
                        : "Current cover picture"}
                    </Typography>
                  </ImagePreviewContainer>
                )}
              </Box>

              <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                <StyledButton
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={
                    apiRequestIsLoading ||
                    (!selectedProfilePicture && !selectedCoverPicture)
                  }
                  startIcon={
                    apiRequestIsLoading && (
                      <CircularProgress size={16} color="inherit" />
                    )
                  }
                >
                  {apiRequestIsLoading ? "Saving..." : "Save Changes"}
                </StyledButton>
              </Box>
            </Box>
          </StyledTabPanel>

          <StyledTabPanel value="3">
            <Box
              component="form"
              noValidate
              autoComplete="off"
              onSubmit={handleChangeOfPassword}
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                <TextField
                  required
                  id="currentPassword"
                  label="Current Password"
                  type={showPassword1 ? "text" : "password"}
                  inputRef={currentPasswordRef}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleShowPassword1}
                          edge="end"
                          aria-label="toggle password visibility"
                        >
                          {showPassword1 ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>

              <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                <TextField
                  required
                  id="newPassword"
                  label="New Password"
                  type={showPassword2 ? "text" : "password"}
                  inputRef={newPasswordRef}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleShowPassword2}
                          edge="end"
                          aria-label="toggle password visibility"
                        >
                          {showPassword2 ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>

              <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                <TextField
                  required
                  id="confirmPassword"
                  label="Confirm New Password"
                  type={showPassword3 ? "text" : "password"}
                  inputRef={confirmPasswordRef}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleShowPassword3}
                          edge="end"
                          aria-label="toggle password visibility"
                        >
                          {showPassword3 ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>

              <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                <StyledButton
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={apiRequestIsLoading}
                  startIcon={
                    apiRequestIsLoading && (
                      <CircularProgress size={16} color="inherit" />
                    )
                  }
                >
                  {apiRequestIsLoading ? "Updating..." : "Update Password"}
                </StyledButton>
              </Box>
            </Box>
          </StyledTabPanel>

          <StyledTabPanel value="4">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <StyledSettingsItem elevation={1}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={500}>
                    Email Notifications
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Receive email updates about account activity
                  </Typography>
                </Box>
                <Switch
                  checked={hasEmailNotification}
                  onChange={handleChangeOfEmailNotificationStatus}
                  color="primary"
                  inputProps={{ "aria-label": "email notification toggle" }}
                />
              </StyledSettingsItem>

              {/* Can add more settings options here in the future */}
            </Box>
          </StyledTabPanel>
        </TabContext>
      </DialogContent>
    </StyledDialog>
  );
}
